<script lang="ts">
  /**
   * The sandbox: a place to reduce terms that is not graded and not saved.
   *
   * Its state lives in the game store rather than here, so that leaving the
   * screen does not throw the term away.
   */
  import { describeLimit, programText } from '../lib/format';
  import { game } from '../state/game.svelte';
  import CodeEditor from './CodeEditor.svelte';
  import Problems from './Problems.svelte';

  const sandbox = $derived(game.sandbox);
  /** The definitions this term reaches; the rest of the library is still in scope. */
  const library = $derived(game.sandboxContext);
  /** Names worth completing: the library's, and the sandbox's own. */
  const names = $derived(game.sandboxSuggestions);
  const problems = $derived(game.sandboxProblems);

  /** How many single steps "Step by step" records before it stops. */
  const STEP_LIMIT = 200;

  let result = $state<string | null>(null);
  let failure = $state<string | null>(null);
  let steps = $state<string[]>([]);
  let busy = $state(false);

  /**
   * Runs one reduction and reports how it went.
   *
   * The worker can die between two requests, so a rejection is a message
   * rather than a button that looks as if nothing happened.
   */
  async function run(action: () => Promise<void>): Promise<void> {
    busy = true;
    failure = null;
    try {
      await action();
    } catch (error) {
      failure = error instanceof Error ? error.message : String(error);
      result = null;
    } finally {
      busy = false;
    }
  }

  async function normalise(): Promise<void> {
    result = null;
    steps = [];
    await run(async () => {
      const response = await game.normalizeInSandbox(sandbox.term);
      if (response.error !== null) {
        failure = response.error;
      } else if (response.limit !== null) {
        failure = `iteration limit exceeded: ${describeLimit(response.limit)}`;
      } else {
        result = response.normalForm;
      }
    });
  }

  async function reduce(): Promise<void> {
    result = null;
    steps = [];
    await run(async () => {
      const response = await game.traceInSandbox(sandbox.term, STEP_LIMIT);
      if (response.error !== null) {
        failure = response.error;
      } else if (response.limit !== null) {
        failure = `iteration limit exceeded: ${describeLimit(response.limit)}`;
      } else {
        steps = response.terms;
        if (!response.completed) {
          failure = `stopped after ${STEP_LIMIT} steps without reaching a normal form`;
        }
      }
    });
  }
</script>

<article class="page sandbox">
  <header>
    <h1>Sandbox</h1>
  </header>

  <label class="use-library">
    <input
      type="checkbox"
      checked={sandbox.useLibrary}
      onchange={(event) => game.setSandbox({ useLibrary: event.currentTarget.checked })}
    />
    use library
  </label>

  {#if library.length > 0}
    <section>
      <div class="section-head">
        library
        <span class="count">used {library.length} definition{library.length === 1 ? '' : 's'}</span>
      </div>
      <CodeEditor
        value={programText(library)}
        names={names}
        readOnly
        lineNumbers={false}
        minHeight="0"
      />
    </section>
  {/if}

  <section>
    <div class="section-head">
      definitions
      <span class="count">optional, used only here</span>
    </div>
    <CodeEditor
      value={sandbox.definitions}
      names={names}
      diagnostics={problems}
      hint="name = term"

      minHeight="4rem"
      onchange={(value) => game.setSandbox({ definitions: value })}
    />
    <Problems diagnostics={problems} source={sandbox.definitions} />
  </section>

  <section>
    <div class="section-head">term to reduce</div>
    <CodeEditor
      value={sandbox.term}
      names={names}
      hint="\this is. an (example (\expression. free)) (variables are) allowed"
      minHeight="4.5rem"
      onchange={(value) => game.setSandbox({ term: value })}
      onsubmit={() => void normalise()}
    />
  </section>

  <div class="actions">
    <button class="primary" disabled={busy || sandbox.term.trim() === ''} onclick={() => void normalise()}>
      Normalise
    </button>
    <button disabled={busy || sandbox.term.trim() === ''} onclick={() => void reduce()}>
      Step by step
    </button>
  </div>

  {#if failure}
    <p class="failure" aria-live="polite">{failure}</p>
  {/if}

  {#if result !== null}
    <section class="result">
      <div class="section-head">normal form</div>
      <code>{result}</code>
    </section>
  {/if}

  {#if steps.length > 0}
    <section class="result">
      <div class="section-head">
        reduction, {steps.length - 1} step{steps.length === 2 ? '' : 's'}
      </div>
      <ol>
        {#each steps as step, index (index)}
          <li><code>{step}</code></li>
        {/each}
      </ol>
    </section>
  {/if}
</article>

<style>
  h1 {
    margin: 0 0 6px;
  }

  section {
    margin-top: 16px;
  }

  .use-library {
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--text);
    font-size: 13px;
    padding: 12px 0 0;
    cursor: pointer;
    width: fit-content;
  }

  .use-library input {
    appearance: none;
    width: 14px;
    height: 14px;
    margin: 0;
    border: 1px solid var(--border-strong);
    border-radius: 3px;
    background: var(--bg-input);
    cursor: pointer;
  }

  .use-library input:checked {
    background: var(--accent);
    border-color: var(--accent);
  }

  .use-library input:checked::after {
    content: '';
    display: block;
    width: 3px;
    height: 7px;
    margin: 1px 0 0 4px;
    border: solid var(--on-accent);
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }

  .section-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-faint);
    padding: 4px 2px 6px;
  }

  .count {
    margin-left: auto;
    text-transform: none;
    letter-spacing: 0;
    font-family: var(--mono);
  }

  .actions {
    display: flex;
    gap: 8px;
    margin-top: 14px;
  }

  .failure {
    color: var(--warn);
    font-size: 13px;
    margin-top: 12px;
  }

  .result code {
    display: block;
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 10px 12px;
    overflow-x: auto;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  ol {
    margin: 0;
    padding-left: 22px;
    display: grid;
    gap: 4px;
  }

  li {
    overflow-wrap: anywhere;
  }
</style>
